import type { ReactNode } from "react";

/**
 * A page inside the shell: a title band, then the body.
 *
 * The band is the wireframes' — the section's name, one sentence saying what the page is for,
 * and a hairline under both — and every screen in the drawings has it, which is what makes the
 * four pages feel like one product rather than four. #302's and #304's screens fill the body;
 * until then the placeholder does.
 *
 * **The shell fills the viewport; the page is a content column; the blurb is a measure.**
 * Long prose is hard to read across a wide monitor, so `68ch` belongs on the paragraph, not
 * on the panel — which is the mistake the first cut made by centring the whole app. Capping
 * only the heading row at that same `68ch`, while the cards under it stretched with the main
 * pane, was the next one: Add sat in the middle of a laptop window, attached to neither.
 * `.screen__column` is `--content` wide so a heading, its action, and the cards share one
 * object. The head itself stays full-bleed — its hairline is a pane divider, not a floating
 * rule — and the shell (header, sidebar, drawer) still reaches every edge.
 *
 * **`action` and `aside` are two different things and the roster needs both** (#303).
 * `Roster.dc.html` puts *Invite someone* on the band itself, opposite the title, because it
 * is the page's one primary act; `Memory.dc.html` has no such control and instead carries the
 * *Whose:* filter under the blurb. So the band takes one slot beside the title and one below
 * the prose, and a screen that wants neither passes neither.
 */
export function Screen({
  title,
  blurb,
  action,
  aside,
  children,
}: {
  title: string;
  blurb?: ReactNode;
  /** A primary control on the band, opposite the title. The roster's *Invite someone*. */
  action?: ReactNode;
  /** Anything the band carries under the prose. The memory screen's *Whose:* filter. */
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="screen">
      <header className="screen__head">
        <div className="screen__column">
          <div className="screen__band">
            <div className="screen__titles">
              <h1 className="screen__title">{title}</h1>
              {blurb === undefined ? null : <p className="screen__blurb">{blurb}</p>}
            </div>
            {action === undefined ? null : <div className="screen__action">{action}</div>}
          </div>
          {aside}
        </div>
      </header>
      <div className="screen__body screen__column">{children}</div>
    </article>
  );
}
